// src/pages/EventsAdmin.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Table } from "react-bootstrap";
import { createEvent, deleteEvent, getEvents, updateEvent } from "../api/api";
import { useNotifications } from "../components/NotificationsContext";
import "../components/AdherentShowcase.css";

const emptyForm = {
  titre: "",
  description: "",
  date_debut: "",
  date_fin: "",
  date_evenement: "",
  lieu: "",
  capacite_max: 0,
  statut: "ouvert",
  actif: true,
  prix: 0,
  subvention_pourcent: 0,
  quota_sur_subvention: false,
  autorise_adherent: true,
  autorise_enfant: false,
  autorise_parent: false,
  age_min_enfant: "",
  age_max_enfant: "",
  duree_paiement_mois: 1,
};

function todayLocalInput() {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 16);
}

function isTodayOrFuture(value) {
  if (!value) return true;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(value) >= today;
}

function imageUrl(ev) {
  if (!ev?.image) return null;
  if (ev.image.startsWith("http://") || ev.image.startsWith("https://")) return ev.image;
  return `http://localhost:8000${ev.image}`;
}

function prixNet(ev) {
  const p = Number(ev.prix || 0);
  const s = Number(ev.subvention_pourcent || 0);
  return (p - (p * s) / 100).toFixed(2);
}

function formatDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleString("fr-FR", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function targets(ev) {
  return [
    ev.autorise_adherent && "Adherent",
    ev.autorise_enfant && "Enfant",
    ev.autorise_parent && "Parent",
  ].filter(Boolean);
}

function quotaImpact(ev) {
  const prix = Number(ev.prix || 0);
  const subvention = Number(ev.subvention_pourcent || 0);
  const montantSubvention = (prix * subvention) / 100;
  return ev.quota_sur_subvention ? montantSubvention.toFixed(2) : "0.00";
}

export default function EventsAdmin({ embedded = false }) {
  const [events, setEvents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [eventStep, setEventStep] = useState(1);
  const [eventConfirmed, setEventConfirmed] = useState(false);
  const { addNotification } = useNotifications();

  const sortedEvents = useMemo(
    () => [...events]
      .filter((event) => isTodayOrFuture(event.date_evenement || event.date_debut))
      .sort((a, b) => new Date((a.date_evenement || a.date_debut) || 0) - new Date((b.date_evenement || b.date_debut) || 0)),
    [events]
  );

  const fetchEvents = async () => {
    try {
      const res = await getEvents();
      setEvents(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      addNotification({
        type: "error",
        title: "Erreur",
        message: "Impossible de charger les evenements.",
        delay: 6000,
      });
    }
  };

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditingEvent(null);
    setForm({ ...emptyForm, date_debut: todayLocalInput(), date_evenement: todayLocalInput() });
    setImageFile(null);
    setImagePreview(null);
    setEventStep(1);
    setEventConfirmed(false);
    setShowModal(true);
  };

  const openEdit = (ev) => {
    setEditingEvent(ev);
    setForm({
      ...emptyForm,
      titre: ev.titre || "",
      description: ev.description || "",
      date_debut: ev.date_debut ? ev.date_debut.slice(0, 16) : "",
      date_fin: ev.date_fin ? ev.date_fin.slice(0, 16) : "",
      date_evenement: ev.date_evenement ? ev.date_evenement.slice(0, 16) : "",
      lieu: ev.lieu || "",
      capacite_max: ev.capacite_max || 0,
      statut: ev.statut || "ouvert",
      actif: ev.actif ?? true,
      prix: ev.prix || 0,
      subvention_pourcent: ev.subvention_pourcent || 0,
      quota_sur_subvention: !!ev.quota_sur_subvention,
      autorise_adherent: !!ev.autorise_adherent,
      autorise_enfant: !!ev.autorise_enfant,
      autorise_parent: !!ev.autorise_parent,
      age_min_enfant: ev.age_min_enfant ?? "",
      age_max_enfant: ev.age_max_enfant ?? "",
      duree_paiement_mois: ev.duree_paiement_mois || 1,
    });
    setImageFile(null);
    setImagePreview(imageUrl(ev));
    setEventStep(1);
    setEventConfirmed(false);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingEvent(null);
    setImageFile(null);
    setImagePreview(null);
    setEventStep(1);
    setEventConfirmed(false);
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0] || null;
    setEventConfirmed(false);
    setImageFile(file);
    if (!file) {
      setImagePreview(editingEvent ? imageUrl(editingEvent) : null);
      return;
    }
    setImagePreview(URL.createObjectURL(file));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEventConfirmed(false);

    if (name === "autorise_enfant" && type === "checkbox" && !checked) {
      setForm((prev) => ({
        ...prev,
        autorise_enfant: false,
        age_min_enfant: "",
        age_max_enfant: "",
      }));
      return;
    }

    if (name === "statut" && value === "ouvert") {
      setForm((prev) => ({
        ...prev,
        statut: value,
        actif: true,
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !form.titre.trim() ||
      Number(form.capacite_max) <= 0 ||
      !form.date_debut ||
      !form.date_evenement ||
      !form.lieu.trim() ||
      (!form.autorise_adherent && !form.autorise_enfant && !form.autorise_parent)
    ) {
      addNotification({
        type: "warning",
        title: "Informations incompletes",
        message: "Completez les etapes obligatoires avant de confirmer l'evenement.",
        delay: 6000,
      });
      setEventStep(1);
      return;
    }

    if (!eventConfirmed) {
      addNotification({
        type: "warning",
        title: "Confirmation requise",
        message: "Confirmez le resume avant d'enregistrer l'evenement.",
        delay: 5000,
      });
      setEventStep(4);
      return;
    }

    const min = form.age_min_enfant === "" ? null : Number(form.age_min_enfant);
    const max = form.age_max_enfant === "" ? null : Number(form.age_max_enfant);
    if (min !== null && max !== null && min > max) {
      addNotification({
        type: "error",
        title: "Erreur",
        message: "Age min enfant ne peut pas etre superieur a age max.",
        delay: 6000,
      });
      return;
    }

    try {
      const fd = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (value === "") return;
        fd.append(key, value);
      });
      if (imageFile) fd.append("image", imageFile);

      const res = editingEvent
        ? await updateEvent(editingEvent.id, fd)
        : await createEvent(fd);

      setEvents((prev) => {
        if (!editingEvent) return [res.data, ...prev];
        return prev.map((ev) => (ev.id === editingEvent.id ? res.data : ev));
      });

      addNotification({
        type: "success",
        title: "Succes",
        message: editingEvent ? "Evenement mis a jour." : "Evenement cree.",
        delay: 4000,
      });

      closeModal();
    } catch (err) {
      addNotification({
        type: "error",
        title: "Erreur",
        message: "Erreur lors de l'enregistrement de l'evenement.",
        delay: 7000,
      });
    }
  };

  const stepLabels = ["Essentiel", "Dates & budget", "Cibles", "Confirmation"];
  const canGoNext = () => {
    if (eventStep === 1) {
      return form.titre.trim() && Number(form.capacite_max) > 0;
    }
    if (eventStep === 2) {
      return form.date_debut && form.date_evenement && form.lieu.trim();
    }
    if (eventStep === 3) {
      return form.autorise_adherent || form.autorise_enfant || form.autorise_parent;
    }
    return eventConfirmed;
  };

  const handleDelete = async (ev) => {
    if (!window.confirm(`Supprimer l'evenement "${ev.titre}" ?`)) return;
    try {
      await deleteEvent(ev.id);
      setEvents((prev) => prev.filter((e) => e.id !== ev.id));
      addNotification({
        type: "success",
        title: "Suppression",
        message: `Evenement "${ev.titre}" supprime.`,
        delay: 4000,
      });
    } catch (err) {
      addNotification({
        type: "error",
        title: "Erreur",
        message: "Echec de la suppression.",
        delay: 6000,
      });
    }
  };

  return (
    <div className={embedded ? "pt-2" : "p-4"}>
      <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
        {!embedded && (
          <div>
            <span className="profile-eyebrow">Administration</span>
            <h4 className="mb-0">Gestion des evenements</h4>
          </div>
        )}
        <Button variant="success" onClick={openCreate}>
          Ajouter un evenement
        </Button>
      </div>

      <Card>
        <Card.Body>
          {sortedEvents.length === 0 ? (
            <Alert variant="warning" className="mb-0">Aucun evenement pour le moment.</Alert>
          ) : (
            <Table responsive hover className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>Evenement</th>
                  <th>Dates</th>
                  <th>Prix net</th>
                  <th>Capacite</th>
                  <th>Cibles</th>
                  <th>Paiement</th>
                  <th style={{ width: 190 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedEvents.map((ev) => (
                  <tr key={ev.id}>
                    <td style={{ minWidth: 260 }}>
                      <div className="d-flex align-items-center gap-3">
                        {imageUrl(ev) ? (
                          <img
                            src={imageUrl(ev)}
                            alt={ev.titre}
                            style={{ width: 88, height: 62, objectFit: "cover", borderRadius: 16 }}
                          />
                        ) : (
                          <div className="showcase-row-placeholder" style={{ width: 88, height: 62 }}>
                            EV
                          </div>
                        )}
                        <div>
                          <div style={{ fontWeight: 900 }}>{ev.titre}</div>
                          <div className="text-muted" style={{ fontSize: 13 }}>{ev.lieu || "-"}</div>
                          <Badge bg={ev.statut === "ouvert" ? "success" : "secondary"} className="mt-1">
                            {ev.statut || "ouvert"}
                          </Badge>
                        </div>
                      </div>
                    </td>
                    <td style={{ minWidth: 220 }}>
                      <div>Evenement: {formatDate(ev.date_evenement || ev.date_debut)}</div>
                      <div className="text-muted" style={{ fontSize: 13 }}>Inscription: {formatDate(ev.date_debut)}</div>
                      <div className="text-muted" style={{ fontSize: 13 }}>Fin: {formatDate(ev.date_fin)}</div>
                    </td>
                    <td>
                      <strong>{prixNet(ev)} DT</strong>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        prix {ev.prix} / subv {ev.subvention_pourcent}%
                      </div>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        quota: {quotaImpact(ev)} DT
                      </div>
                    </td>
                    <td>
                      <Badge bg="success">{ev.nb_inscrits || 0} inscrits</Badge>
                      <div className="text-muted" style={{ fontSize: 13 }}>
                        max {ev.capacite_max || 0}
                      </div>
                    </td>
                    <td>
                      {targets(ev).map((target) => (
                        <Badge key={target} bg="light" text="dark" className="me-1 mb-1">
                          {target}
                        </Badge>
                      ))}
                    </td>
                    <td>
                      <Badge bg="warning" text="dark">
                        {ev.duree_paiement_mois || 1} mois
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex gap-2 flex-wrap">
                        <Button size="sm" variant="outline-primary" onClick={() => openEdit(ev)}>
                          Modifier
                        </Button>
                        <Button size="sm" variant="outline-danger" onClick={() => handleDelete(ev)}>
                          Supprimer
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={closeModal} size="lg" centered dialogClassName="event-wizard-modal">
        <Modal.Header closeButton>
          <Modal.Title>{editingEvent ? "Modifier l'evenement" : "Ajouter un evenement"}</Modal.Title>
        </Modal.Header>

        <Form onSubmit={handleSubmit} encType="multipart/form-data">
          <Modal.Body>
            <div className="event-wizard-steps">
              {stepLabels.map((label, index) => (
                <button
                  type="button"
                  key={label}
                  className={eventStep === index + 1 ? "active" : eventStep > index + 1 ? "done" : ""}
                  onClick={() => setEventStep(index + 1)}
                >
                  <span>{index + 1}</span>
                  {label}
                </button>
              ))}
            </div>

            {eventStep === 1 && (
              <div className="event-wizard-page">
                <Row className="g-3">
                  <Col md={8}>
                    <Form.Group>
                      <Form.Label>Titre</Form.Label>
                      <Form.Control type="text" name="titre" value={form.titre} onChange={handleChange} required />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Capacite max</Form.Label>
                      <Form.Control type="number" name="capacite_max" value={form.capacite_max} onChange={handleChange} required min="1" />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Statut</Form.Label>
                      <Form.Select name="statut" value={form.statut} onChange={handleChange}>
                        <option value="ouvert">Ouvert</option>
                        <option value="brouillon">Brouillon</option>
                        <option value="complet">Complet</option>
                        <option value="termine">Termine</option>
                        <option value="annule">Annule</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6} className="d-flex align-items-end">
                    <Form.Check
                      type="switch"
                      label="Visible aux adherents"
                      name="actif"
                      checked={form.actif}
                      disabled={form.statut === "ouvert"}
                      onChange={handleChange}
                    />
                  </Col>
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label>Description</Form.Label>
                      <Form.Control as="textarea" rows={4} name="description" value={form.description} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col xs={12}>
                    <Form.Label>Image</Form.Label>
                    <label className="modern-upload">
                      <input type="file" accept="image/*" onChange={handleImageChange} />
                      <div>
                        <strong>Televerser un fichier</strong>
                        <span>PNG, JPG ou WEBP. Apercu instantane apres selection.</span>
                      </div>
                      {imagePreview ? (
                        <img src={imagePreview} alt="Apercu evenement" />
                      ) : (
                        <em>Image</em>
                      )}
                    </label>
                  </Col>
                </Row>
              </div>
            )}

            {eventStep === 2 && (
              <div className="event-wizard-page">
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Date debut inscription</Form.Label>
                      <Form.Control type="datetime-local" name="date_debut" value={form.date_debut} onChange={handleChange} required min={todayLocalInput()} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Date fin inscription</Form.Label>
                      <Form.Control type="datetime-local" name="date_fin" value={form.date_fin} onChange={handleChange} min={form.date_debut || todayLocalInput()} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Date de l'evenement</Form.Label>
                      <Form.Control type="datetime-local" name="date_evenement" value={form.date_evenement} onChange={handleChange} required min={todayLocalInput()} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Duree de paiement (mois)</Form.Label>
                      <Form.Control type="number" name="duree_paiement_mois" value={form.duree_paiement_mois} onChange={handleChange} min="1" max="24" required />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Lieu</Form.Label>
                      <Form.Control type="text" name="lieu" value={form.lieu} onChange={handleChange} required />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Prix (DT)</Form.Label>
                      <Form.Control type="number" step="0.01" name="prix" value={form.prix} onChange={handleChange} required min="0" />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Subvention %</Form.Label>
                      <Form.Control type="number" step="0.01" name="subvention_pourcent" value={form.subvention_pourcent} onChange={handleChange} min="0" max="100" />
                    </Form.Group>
                  </Col>
                  <Col xs={12}>
                    <Form.Check
                      type="checkbox"
                      label="Deduction quota sur subvention seulement"
                      name="quota_sur_subvention"
                      checked={form.quota_sur_subvention}
                      onChange={handleChange}
                    />
                    <Alert variant="light" className="event-wizard-note mt-2 mb-0">
                      Si coche: evenement 100 DT avec 20% subvention deduit 20 DT du quota.
                      Sinon: il ne deduit rien du quota.
                    </Alert>
                  </Col>
                </Row>
              </div>
            )}

            {eventStep === 3 && (
              <div className="event-wizard-page">
                <Row className="g-3">
                  <Col md={12}>
                    <Form.Label>Cibles autorisees</Form.Label>
                    <div className="target-choice-grid">
                      <Form.Check type="checkbox" label="Adherent" name="autorise_adherent" checked={form.autorise_adherent} onChange={handleChange} />
                      <Form.Check type="checkbox" label="Enfant" name="autorise_enfant" checked={form.autorise_enfant} onChange={handleChange} />
                      <Form.Check type="checkbox" label="Parent" name="autorise_parent" checked={form.autorise_parent} onChange={handleChange} />
                    </div>
                    {form.autorise_enfant && (
                      <Row className="g-3 mt-1">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Age minimum enfant</Form.Label>
                            <Form.Control type="number" min="0" name="age_min_enfant" value={form.age_min_enfant} onChange={handleChange} placeholder="Ex: 5" />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Age maximum enfant</Form.Label>
                            <Form.Control type="number" min="0" name="age_max_enfant" value={form.age_max_enfant} onChange={handleChange} placeholder="Ex: 12" />
                          </Form.Group>
                        </Col>
                      </Row>
                    )}
                  </Col>
                </Row>
              </div>
            )}

            {eventStep === 4 && (
              <div className="event-wizard-page">
                <Row className="g-3">
                  <Col md={7}>
                    <div className="event-review-card event-review-card--final">
                      <span>Resume final</span>
                      <strong>{form.titre || "Nouvel evenement"}</strong>
                      <small>{form.description || "Aucune description renseignee."}</small>
                      <dl>
                        <div>
                          <dt>Lieu</dt>
                          <dd>{form.lieu || "-"}</dd>
                        </div>
                        <div>
                          <dt>Evenement</dt>
                          <dd>{formatDate(form.date_evenement)}</dd>
                        </div>
                        <div>
                          <dt>Inscriptions</dt>
                          <dd>{formatDate(form.date_debut)} -> {formatDate(form.date_fin)}</dd>
                        </div>
                        <div>
                          <dt>Budget</dt>
                          <dd>{prixNet(form)} DT net, quota {quotaImpact(form)} DT</dd>
                        </div>
                        <div>
                          <dt>Cibles</dt>
                          <dd>{targets(form).join(", ") || "Aucune cible"}</dd>
                        </div>
                      </dl>
                      <div>
                        <Badge bg="success">{form.statut}</Badge>
                        <Badge bg="light" text="dark">{form.capacite_max || 0} places</Badge>
                        <Badge bg="light" text="dark">{form.duree_paiement_mois || 1} mois</Badge>
                      </div>
                    </div>
                  </Col>
                  <Col md={5}>
                    <div className="event-confirm-panel">
                      {imagePreview ? (
                        <img src={imagePreview} alt="Apercu final evenement" />
                      ) : (
                        <div className="event-confirm-panel__placeholder">EV</div>
                      )}
                      <Form.Check
                        type="checkbox"
                        label="Je confirme que les informations de l'evenement sont correctes."
                        checked={eventConfirmed}
                        onChange={(event) => setEventConfirmed(event.target.checked)}
                      />
                      <Alert variant={eventConfirmed ? "success" : "warning"} className="mt-3 mb-0">
                        {eventConfirmed
                          ? "Pret a enregistrer."
                          : "Cochez la confirmation pour finaliser l'evenement."}
                      </Alert>
                    </div>
                  </Col>
                </Row>
              </div>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>Annuler</Button>
            {eventStep > 1 && (
              <Button type="button" variant="outline-secondary" onClick={() => setEventStep((step) => step - 1)}>
                Precedent
              </Button>
            )}
            {eventStep < 4 ? (
              <Button type="button" variant="primary" disabled={!canGoNext()} onClick={() => setEventStep((step) => step + 1)}>
                Continuer
              </Button>
            ) : (
              <Button type="submit" variant="primary" disabled={!eventConfirmed}>Enregistrer</Button>
            )}
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
